import React, { createContext, useContext, useState, useCallback } from "react";
import { Outlet } from "react-router-dom";
import {
  SuperAdminContextType,
  BusinessStats,
  Client,
  AnalyticsData,
  SystemSettings,
} from "../types/super-admin.types";
import axiosInstance from "../../../middlewares/axiosInstance";

const SuperAdminContext = createContext<SuperAdminContextType | null>(null);

export const SuperAdminProviderWithChildren: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [businesses, setBusinesses] = useState<Client[]>([]);
  const [totalBusinesses, setTotalBusinesses] = useState(0);
  const [businessStats, setBusinessStats] = useState<
    Record<string, BusinessStats>
  >({});
  const [systemStats, setSystemStats] = useState<any>(null);
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(
    null
  );

  const fetchBusinesses = useCallback(async (params?: any) => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.post(
        "/super-admin/businesses/list",
        params
      );

      setBusinesses(response.data.data.businesses);
      setTotalBusinesses(response.data.data.total);

      // Fetch stats for each business
      const stats: Record<string, BusinessStats> = {};
      await Promise.all(
        response.data.data.businesses.map(async (business: Client) => {
          const statsResponse = await axiosInstance.post(
            `/super-admin/businesses/${business.id}/stats`
          );
          stats[business.id] = statsResponse.data;
        })
      );
      setBusinessStats(stats);
    } catch (error) {
      console.error("Error fetching businesses:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchSystemStats = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.post("/super-admin/stats");
      setSystemStats(response.data);
    } catch (error) {
      console.error("Error fetching system stats:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const toggleBusinessStatus = useCallback(
    async (businessId: string, activate: boolean) => {
      setIsLoading(true);
      try {
        const response = await axiosInstance.post(
          `/super-admin/businesses/${businessId}/${
            activate ? "activate" : "suspend"
          }`
        );

        setBusinesses((prev) =>
          prev.map((business) =>
            business.id === businessId
              ? { ...business, is_active: activate }
              : business
          )
        );

        return response.data;
      } catch (error) {
        console.error("Error toggling business status:", error);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const fetchAnalytics = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.post("/super-admin/analytics");
      setAnalyticsData(response.data);
    } catch (error) {
      console.error("Error fetching analytics:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateSystemSettings = useCallback(
    async (settings: Partial<SystemSettings>) => {
      setIsLoading(true);
      try {
        const response = await axiosInstance.post(
          "/super-admin/settings/update",
          settings
        );
        setSystemStats((prev: any) => ({
          ...prev,
          ...response.data,
        }));
        return response.data;
      } catch (error) {
        console.error("Error updating system settings:", error);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  return (
    <SuperAdminContext.Provider
      value={{
        isLoading,
        systemStats,
        businesses,
        totalBusinesses,
        businessStats,
        analyticsData,
        systemSettings: systemStats,
        fetchSystemStats,
        fetchBusinesses,
        toggleBusinessStatus,
        fetchAnalytics,
        updateSystemSettings,
      }}
    >
      {children}
    </SuperAdminContext.Provider>
  );
};

export const SuperAdminProvider: React.FC = () => {
  return (
    <SuperAdminProviderWithChildren>
      <SuperAdminOutlet />
    </SuperAdminProviderWithChildren>
  );
};

const SuperAdminOutlet: React.FC = () => {
  return <Outlet />;
};

export const useSuperAdminContext = () => {
  const context = useContext(SuperAdminContext);
  if (!context) {
    throw new Error(
      "useSuperAdminContext must be used within SuperAdminProvider"
    );
  }
  return context;
};
